import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Paper, keyframes } from '@mui/material';
import backgroundImage from '@/assets/ryan-grice-PNcrOr6q6Tk-unsplash.jpg';

const dvdBounce = keyframes`
  0% { top: 0%; left: 0%; }
  12.5% { top: 80%; left: 25%; }
  25% { top: 0%; left: 50%; }
  37.5% { top: 80%; left: 75%; }
  50% { top: 0%; left: 100%; transform: translateX(-100%); }
  62.5% { top: 80%; left: 75%; }
  75% { top: 0%; left: 50%; }
  87.5% { top: 80%; left: 25%; }
  100% { top: 0%; left: 0%; }
`;

interface BouncingFact {
  id: number;
  text: string;
  startTop: number;
  startLeft: number;
  delay: number;
}

const allFacts = [
  "Frogs can absorb water through their skin, so they don't need to drink.",
  "A group of frogs is called an army.",
  "Frogs have been around for over 200 million years.",
  "The golden poison frog has enough toxin to kill 10 grown men.",
  "Some frogs can jump over 20 times their own body length.",
  "Frogs completely shed their skin about once a week and usually eat it.",
  "The glass frog has translucent skin so you can see its organs.",
  "Frogs hibernate in winter by slowing their metabolism almost to a stop.",
  "A frog's eyes help it swallow food by pushing it down its throat.",
  "The world's smallest frog is only 7.7mm long.",
];

const kermitImages = [
  'https://media1.giphy.com/media/DBPF1cNxSwrziGneX7/giphy.gif',
  'https://media4.giphy.com/media/13RKWw9oA5o3GU/giphy.gif',
  'https://media0.giphy.com/media/etOX3h7ApZuDe7Fc5w/giphy.gif',
  'https://media1.giphy.com/media/3oriNSf2iLjMVO7dao/giphy.gif',
  'https://media3.giphy.com/media/XIqCQx02E1U9W/giphy.gif',
  'https://media3.giphy.com/media/7K95K2SuBOaBaXXlGH/giphy.gif',
  'https://media3.giphy.com/media/xbM3kDPOp69iw/giphy.gif',
  'https://media0.giphy.com/media/oNzgBSYx6puFy/giphy.gif',
  'https://media3.giphy.com/media/3o85xGocUH8RYoDKKs/giphy.gif',
];

// Muppets/Sesame Street video playlist - verified video IDs from Muppet Wiki
const muppetVideos = [
  { id: 'pI5QX13eluQ', title: 'Do-Op Hop' },
  { id: 'Q6NQcO9KTBY', title: "Movin' Right Along" },
  { id: 'Mh85R-S-dh8', title: 'Rubber Duckie' },
  { id: 'jopp6CD6HgY', title: 'Fruit' },
  { id: 'jMIK7iP-Sc8', title: 'Mahna Mahna' },
  { id: '51BQfPeSK8k', title: "It's Not Easy Being Green" },
  { id: 'awhyiBv-oQc', title: 'Rainbow Connection' },
];

const SECRET_VIDEO_ID = '1PyM_1HF_3E';

// Fisher-Yates shuffle
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const FregPage: React.FC = () => {
  const [showKermit, setShowKermit] = useState(false);
  const [bouncingFacts, setBouncingFacts] = useState<BouncingFact[]>([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [imageClickCount, setImageClickCount] = useState(0);
  const [lastClickedImage, setLastClickedImage] = useState<number | null>(null);
  const [secretVideoKey, setSecretVideoKey] = useState(0);
  const [shuffledVideos, setShuffledVideos] = useState(muppetVideos);
  const [isPlayingSecret, setIsPlayingSecret] = useState(false);
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT) return;

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
  }, []);

  const onPlayerStateChange = useCallback((event: any) => {
    // YT.PlayerState.ENDED === 0
    if (event.data === 0 && isPlayingSecret) {
      // Secret video ended, shuffle and play random song
      const newShuffled = shuffleArray(muppetVideos);
      setShuffledVideos(newShuffled);
      setIsPlayingSecret(false);
      setSecretVideoKey(0);
      setCurrentVideoIndex(0);

      // Load the shuffled playlist
      if (playerRef.current) {
        playerRef.current.loadVideoById(newShuffled[0].id);
      }
    }
  }, [isPlayingSecret]);

  // Initialize or update YouTube player
  useEffect(() => {
    if (!showKermit || !containerRef.current) return;

    const initPlayer = () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }

      const videoId = isPlayingSecret ? SECRET_VIDEO_ID : shuffledVideos[currentVideoIndex].id;
      const playlist = isPlayingSecret ? undefined : shuffledVideos.map(v => v.id).join(',');

      playerRef.current = new window.YT.Player(containerRef.current, {
        width: '100%',
        height: '100%',
        videoId,
        playerVars: {
          autoplay: 1,
          loop: isPlayingSecret ? 0 : 1,
          playlist: playlist,
        },
        events: {
          onStateChange: onPlayerStateChange,
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [showKermit, secretVideoKey, currentVideoIndex, isPlayingSecret, shuffledVideos, onPlayerStateChange]);

  const handleImageClick = (imageIndex: number) => {
    // Track consecutive clicks on same image for secret video easter egg
    if (lastClickedImage === imageIndex) {
      const newCount = imageClickCount + 1;
      setImageClickCount(newCount);
      if (newCount >= 5) {
        setSecretVideoKey(prev => prev + 1);
        setIsPlayingSecret(true);
        setImageClickCount(0);
        setLastClickedImage(null);
      }
    } else {
      setLastClickedImage(imageIndex);
      setImageClickCount(1);
    }

    const randomFact = allFacts[Math.floor(Math.random() * allFacts.length)];
    const factId = Date.now();
    const newFact: BouncingFact = {
      id: factId,
      text: randomFact,
      startTop: Math.random() * 60,
      startLeft: Math.random() * 80,
      delay: Math.random() * 2,
    };
    setBouncingFacts(prev => [...prev, newFact]);

    // Remove fact after 25 seconds
    setTimeout(() => {
      setBouncingFacts(prev => prev.filter(f => f.id !== factId));
    }, 25000);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        gap: 4,
        minHeight: '100vh',
        px: 2,
        // Fixed background layer that works on mobile
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1,
        },
      }}
    >
      {/* Center Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <span
          role="img"
          aria-label="frog"
          style={{ fontSize: 80, cursor: 'pointer' }}
          onClick={() => setShowKermit(!showKermit)}
        >
          🐸
        </span>
        <Typography variant="h3" sx={{ color: 'white' }}>Freg</Typography>
        <Typography variant="body1" sx={{ color: 'white' }}>
          {showKermit ? "It's not easy being green" : 'Ribbit'}
        </Typography>

        {showKermit && (
          <>
            {/* YouTube Video Playlist */}
            <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', width: '100%', maxWidth: 560, aspectRatio: '16/9' }}>
              <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            </Box>

            {/* Video selector buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 1, maxWidth: 600 }}>
              {shuffledVideos.map((video, index) => (
                <Box
                  key={video.id}
                  onClick={() => {
                    setCurrentVideoIndex(index);
                    setSecretVideoKey(0);
                    setIsPlayingSecret(false);
                    if (playerRef.current) {
                      playerRef.current.loadVideoById(video.id);
                    }
                  }}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: currentVideoIndex === index && !isPlayingSecret ? '#4caf50' : '#e0e0e0',
                    color: currentVideoIndex === index && !isPlayingSecret ? 'white' : 'text.primary',
                    fontSize: '0.85rem',
                    fontWeight: currentVideoIndex === index && !isPlayingSecret ? 'bold' : 'normal',
                    '&:hover': {
                      bgcolor: currentVideoIndex === index && !isPlayingSecret ? '#388e3c' : '#bdbdbd',
                    },
                  }}
                >
                  {video.title}
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mt: 2, maxWidth: 600 }}>
              {kermitImages.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`Kermit ${index + 1}`}
                style={{ borderRadius: 8, maxWidth: 200, maxHeight: 200, objectFit: 'cover', cursor: 'pointer' }}
                onClick={() => handleImageClick(index)}
              />
            ))}
            </Box>
          </>
        )}
      </Box>

      {/* Photo Credit - only show on initial view */}
      {!showKermit && (
        <Typography
          variant="caption"
          sx={{
            position: 'fixed',
            bottom: 8,
            right: 8,
            color: 'rgba(255, 255, 255, 0.7)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
            '& a': {
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            },
          }}
        >
          Photo by{' '}
          <a
            href="https://unsplash.com/@gricey_visuals?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ryan Grice
          </a>{' '}
          on{' '}
          <a
            href="https://unsplash.com/photos/green-trees-on-brown-soil-PNcrOr6q6Tk?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
            target="_blank"
            rel="noopener noreferrer"
          >
            Unsplash
          </a>
        </Typography>
      )}

      {/* Bouncing Facts */}
      {bouncingFacts.map((fact) => (
        <Paper
          key={fact.id}
          sx={{
            position: 'fixed',
            top: `${fact.startTop}%`,
            left: `${fact.startLeft}%`,
            p: 2,
            bgcolor: '#4caf50',
            color: 'white',
            maxWidth: 250,
            zIndex: 1000,
            animation: `${dvdBounce} 25s ease-in-out infinite`,
            willChange: 'top, left',
            transform: 'translateZ(0)',
            animationDelay: `${fact.delay}s`,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            🐸 {fact.text}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default FregPage;
